// ══════════════════════════════════════════════════════════════════════════════
// SENSITIVITY OVERLAY
// Stress-tests a base ScoringInput across realistic downside vectors and
// returns structured impact data (score, DSCR, NOI, IRR, verdict, severity).
// Decision-oriented — not a spreadsheet.
// ══════════════════════════════════════════════════════════════════════════════

import {
  calculateScore,
  type ScoringInput,
  type ScoringResult,
} from "./scoring-engine";

export type StressGroup =
  | "rent"
  | "vacancy"
  | "capex"
  | "expenses"
  | "exit_cap"
  | "interest_rate";

export type StressSeverity = "minor" | "moderate" | "major" | "breaks";

export interface StressOverlay {
  id: string;
  group: StressGroup;
  groupLabel: string;
  label: string;             // "Market rent −10%"
  description: string;       // human-readable assumption shift
  applicable: boolean;       // false if base data is missing
  // Outcomes
  baseScore: number;
  stressedScore: number;
  scoreDelta: number;        // negative = worse
  baseDscr: number | null;
  stressedDscr: number | null;
  dscrDelta: number | null;
  baseNoi: number | null;
  stressedNoi: number | null;
  noiDeltaPct: number | null;
  baseIrr: number | null;
  stressedIrr: number | null;
  irrDelta: number | null;
  baseVerdict: string;
  stressedVerdict: string;
  verdictFlipped: boolean;
  severity: StressSeverity;
}

export interface SensitivityOverlayResult {
  base: {
    score: number;
    verdict: string;
    dscr: number | null;
    noi: number | null;
    irr: number | null;
  };
  overlays: StressOverlay[];
  byGroup: Partial<Record<StressGroup, StressOverlay[]>>;
  worst: StressOverlay | null;       // "Where the deal breaks"
  flipCount: number;                 // overlays that flipped verdict
  applicableCount: number;
  /** Fragility under baseline assumptions (independent of stress flips). */
  fragility: {
    isFragile: boolean;
    reasons: string[];
  };
}

export interface SensitivityComputeOptions {
  /** Final orchestrator decision verdict (GO / CAUTION / PASS). Used to align messaging. */
  decisionVerdict?: string | null;
  /** Aggregate data confidence score (0–100) — low confidence flags fragility. */
  dataConfidence?: number | null;
  /** Optional NOI trend signal from T12 ("declining" → fragile). */
  noiTrend?: string | null;
}

const GROUP_LABEL: Record<StressGroup, string> = {
  rent: "Rent Downside",
  vacancy: "Vacancy Stress",
  capex: "CapEx Overrun",
  expenses: "Expense Stress",
  exit_cap: "Exit Cap Expansion",
  interest_rate: "Interest Rate Stress",
};

interface StressDef {
  id: string;
  group: StressGroup;
  label: string;
  buildDescription: (base: ScoringInput) => string;
  isApplicable: (base: ScoringInput) => boolean;
  apply: (base: ScoringInput) => ScoringInput;
}

// ── Helpers ────────────────────────────────────────────────────────────────
function nz(v: number | string | undefined | null, fallback = 0): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function clone(input: ScoringInput): ScoringInput {
  return { ...input };
}

function effectiveStabilizedRent(base: ScoringInput): number {
  if (base.stabilized_rent && base.stabilized_rent > 0) return base.stabilized_rent;
  if (base.avg_in_place_rent && base.rent_premium_pct != null) {
    return base.avg_in_place_rent * (1 + base.rent_premium_pct / 100);
  }
  if (base.market_rent_estimate && base.market_rent_estimate > 0) return base.market_rent_estimate;
  return base.avg_in_place_rent || 0;
}

/**
 * Apply a proportional revenue scale (e.g. 0.95 for −5%) so the engine
 * recomputes NOI under stress even when T12-grounded baseline_total_income
 * or net_operating_income are present.
 */
function scaleRevenue(next: ScoringInput, factor: number): void {
  if (typeof next.baseline_total_income === "number" && next.baseline_total_income > 0) {
    next.baseline_total_income = next.baseline_total_income * factor;
  }
  if (typeof next.gross_potential_rent === "number" && next.gross_potential_rent > 0) {
    next.gross_potential_rent = next.gross_potential_rent * factor;
  }
  if (typeof next.other_income === "number" && next.other_income > 0) {
    next.other_income = next.other_income * factor;
  }
  // Drop any pre-baked NOI so the engine recomputes from stressed income/expenses.
  delete next.net_operating_income;
}

/**
 * Apply a proportional expense scale (e.g. 1.05 for +5%) so the engine
 * recomputes NOI under stress even when baseline_expenses /
 * total_operating_expenses are T12-grounded.
 */
function scaleExpenses(next: ScoringInput, factor: number): void {
  let touched = false;
  if (typeof next.baseline_expenses === "number" && next.baseline_expenses > 0) {
    next.baseline_expenses = next.baseline_expenses * factor;
    touched = true;
  }
  if (typeof next.total_operating_expenses === "number" && next.total_operating_expenses > 0) {
    next.total_operating_expenses = next.total_operating_expenses * factor;
    touched = true;
  }
  if (typeof next.per_unit_expenses === "number" && next.per_unit_expenses > 0) {
    next.per_unit_expenses = next.per_unit_expenses * factor;
    touched = true;
  }
  if (typeof next.expense_ratio === "number" && next.expense_ratio > 0) {
    next.expense_ratio = next.expense_ratio * factor;
    touched = true;
  }
  if (touched) {
    delete next.net_operating_income;
  }
}

/**
 * For vacancy stress: the engine short-circuits to baseline_total_income
 * (T12 EGI) when present, which already has historical vacancy baked in.
 * To layer an *additional* vacancy stress on top, scale the grounded EGI
 * proportionally — (1 - newVac) / (1 - baselineVac) — and clear pre-baked
 * NOI so it is recomputed from the stressed EGI. If no T12 EGI is grounded,
 * the engine will naturally apply vacancy_rate to the rebuilt GPI.
 */
function unlockForVacancyStress(next: ScoringInput, baselineVacPct: number): void {
  if (typeof next.baseline_total_income === "number" && next.baseline_total_income > 0) {
    const newVacPct = nz(next.vacancy_rate, 5) / 100;
    const ratio = (1 - newVacPct) / (1 - baselineVacPct);
    if (Number.isFinite(ratio) && ratio > 0 && ratio < 1) {
      next.baseline_total_income = next.baseline_total_income * ratio;
    } else if (ratio <= 0) {
      next.baseline_total_income = 0;
    }
  }
  delete next.net_operating_income;
}

// ── Stress Definitions ─────────────────────────────────────────────────────
const STRESSES: StressDef[] = [
  // 1. Rent downside
  {
    id: "rent_minus_5",
    group: "rent",
    label: "Market rent −5%",
    buildDescription: (b) => {
      const r = effectiveStabilizedRent(b);
      return r > 0 ? `Stabilized rent to $${Math.round(r * 0.95).toLocaleString()}/mo` : "Stabilized rent reduced by 5%";
    },
    isApplicable: (b) => effectiveStabilizedRent(b) > 0,
    apply: (b) => {
      const next = clone(b);
      const cur = effectiveStabilizedRent(b);
      next.stabilized_rent = cur * 0.95;
      // Decouple from rent_premium so the engine uses our explicit value
      delete next.rent_premium_pct;
      // Propagate revenue stress through any T12-grounded EGI / GPR / NOI.
      scaleRevenue(next, 0.95);
      return next;
    },
  },
  {
    id: "rent_minus_10",
    group: "rent",
    label: "Market rent −10%",
    buildDescription: (b) => {
      const r = effectiveStabilizedRent(b);
      return r > 0 ? `Stabilized rent to $${Math.round(r * 0.90).toLocaleString()}/mo` : "Stabilized rent reduced by 10%";
    },
    isApplicable: (b) => effectiveStabilizedRent(b) > 0,
    apply: (b) => {
      const next = clone(b);
      const cur = effectiveStabilizedRent(b);
      next.stabilized_rent = cur * 0.90;
      delete next.rent_premium_pct;
      scaleRevenue(next, 0.90);
      return next;
    },
  },
  // 2. Vacancy
  {
    id: "vacancy_plus_2",
    group: "vacancy",
    label: "Vacancy +2 pts",
    buildDescription: (b) => `Stabilized vacancy to ${(nz(b.vacancy_rate, 5) + 2).toFixed(1)}%`,
    isApplicable: () => true,
    apply: (b) => {
      const next = clone(b);
      const baseVac = nz(b.vacancy_rate, 5) / 100;
      next.vacancy_rate = nz(b.vacancy_rate, 5) + 2;
      unlockForVacancyStress(next, baseVac);
      return next;
    },
  },
  {
    id: "vacancy_plus_5",
    group: "vacancy",
    label: "Vacancy +5 pts",
    buildDescription: (b) => `Stabilized vacancy to ${(nz(b.vacancy_rate, 5) + 5).toFixed(1)}%`,
    isApplicable: () => true,
    apply: (b) => {
      const next = clone(b);
      const baseVac = nz(b.vacancy_rate, 5) / 100;
      next.vacancy_rate = nz(b.vacancy_rate, 5) + 5;
      unlockForVacancyStress(next, baseVac);
      return next;
    },
  },
  // 3. CapEx
  {
    id: "capex_plus_10",
    group: "capex",
    label: "CapEx +10%",
    buildDescription: (b) => {
      const c = nz(b.capex_total) || nz(b.capex_reserves);
      return c > 0 ? `Budget +$${Math.round(c * 0.1).toLocaleString()}` : "CapEx budget +10%";
    },
    isApplicable: (b) => (nz(b.capex_total) || nz(b.capex_reserves)) > 0,
    apply: (b) => {
      const cur = nz(b.capex_total) || nz(b.capex_reserves);
      return { ...b, capex_total: cur * 1.10 };
    },
  },
  {
    id: "capex_plus_20",
    group: "capex",
    label: "CapEx +20%",
    buildDescription: (b) => {
      const c = nz(b.capex_total) || nz(b.capex_reserves);
      return c > 0 ? `Budget +$${Math.round(c * 0.2).toLocaleString()}` : "CapEx budget +20%";
    },
    isApplicable: (b) => (nz(b.capex_total) || nz(b.capex_reserves)) > 0,
    apply: (b) => {
      const cur = nz(b.capex_total) || nz(b.capex_reserves);
      return { ...b, capex_total: cur * 1.20 };
    },
  },
  // 4. Expenses
  {
    id: "expenses_plus_5",
    group: "expenses",
    label: "Expenses +5%",
    buildDescription: () => "Operating expenses +5%",
    isApplicable: (b) =>
      nz(b.total_operating_expenses) > 0 ||
      nz(b.expense_ratio) > 0 ||
      nz(b.per_unit_expenses) > 0 ||
      nz(b.baseline_expenses) > 0,
    apply: (b) => {
      const next = clone(b);
      scaleExpenses(next, 1.05);
      return next;
    },
  },
  {
    id: "expenses_plus_10",
    group: "expenses",
    label: "Expenses +10%",
    buildDescription: () => "Operating expenses +10%",
    isApplicable: (b) =>
      nz(b.total_operating_expenses) > 0 ||
      nz(b.expense_ratio) > 0 ||
      nz(b.per_unit_expenses) > 0 ||
      nz(b.baseline_expenses) > 0,
    apply: (b) => {
      const next = clone(b);
      scaleExpenses(next, 1.10);
      return next;
    },
  },
  // 5. Exit cap
  {
    id: "exit_cap_plus_25",
    group: "exit_cap",
    label: "Exit cap +25 bps",
    buildDescription: (b) => {
      const c = nz(b.exit_cap);
      return c > 0 ? `Exit cap to ${(c + 0.25).toFixed(2)}%` : "Exit cap +25 bps";
    },
    isApplicable: (b) => nz(b.exit_cap) > 0,
    apply: (b) => ({ ...b, exit_cap: nz(b.exit_cap) + 0.25 }),
  },
  {
    id: "exit_cap_plus_50",
    group: "exit_cap",
    label: "Exit cap +50 bps",
    buildDescription: (b) => {
      const c = nz(b.exit_cap);
      return c > 0 ? `Exit cap to ${(c + 0.50).toFixed(2)}%` : "Exit cap +50 bps";
    },
    isApplicable: (b) => nz(b.exit_cap) > 0,
    apply: (b) => ({ ...b, exit_cap: nz(b.exit_cap) + 0.50 }),
  },
  // 6. Interest rate
  {
    id: "rate_plus_50",
    group: "interest_rate",
    label: "Rate +50 bps",
    buildDescription: (b) => {
      const r = nz(b.interest_rate);
      return r > 0 ? `Interest rate to ${(r + 0.5).toFixed(2)}%` : "Interest rate +50 bps";
    },
    isApplicable: (b) => nz(b.interest_rate) > 0,
    apply: (b) => ({ ...b, interest_rate: nz(b.interest_rate) + 0.5 }),
  },
  {
    id: "rate_plus_100",
    group: "interest_rate",
    label: "Rate +100 bps",
    buildDescription: (b) => {
      const r = nz(b.interest_rate);
      return r > 0 ? `Interest rate to ${(r + 1).toFixed(2)}%` : "Interest rate +100 bps";
    },
    isApplicable: (b) => nz(b.interest_rate) > 0,
    apply: (b) => ({ ...b, interest_rate: nz(b.interest_rate) + 1 }),
  },
];

// ── Break condition — survival, not performance ────────────────────────────
// Score at or below which a stressed scenario counts as collapsed. Mirrors
// SCORE_COLLAPSE_FLOOR in the engine's global break detector (scoring-core).
const SCORE_COLLAPSE_FLOOR = 10;

/**
 * Does this scenario break the deal?
 *
 * Same rule as the engine's `scenarioBreaksDeal`: coverage is the survival
 * test, the score is a performance measure, and conflating them is what made
 * the engine veto sound deals before it was corrected. A scenario breaks when
 * the deal can no longer service its debt, or when the score collapses
 * outright — never because the verdict fell a notch. Every levered deal has a
 * bad downside case; that is what a stress test is for.
 *
 * Keeping this identical to the engine matters for the same reason the score
 * lives in one place: two definitions of "breaks" is the two-engines bug, one
 * layer up.
 */
function scenarioBreaks(stressedScore: number, stressedDscr: number | null): boolean {
  if (stressedDscr != null && stressedDscr > 0 && stressedDscr < 1.0) return true;
  return stressedScore <= SCORE_COLLAPSE_FLOOR;
}

// ── Severity classification ────────────────────────────────────────────────
// Severity reflects the *magnitude of additional damage* from the stress,
// not the absolute condition of the deal. Use score-floor-resilient signals
// (NOI / DSCR magnitudes) so severity stays meaningful when the score is
// pinned. "Breaks" is not decided here — `scenarioBreaks` owns it, and a
// scenario only reaches this function when the deal survives it.
function classifySeverity(
  scoreDelta: number,
  verdictFlipped: boolean,
  stressedDscr: number | null,
  baseDscr: number | null,
  noiDeltaPct: number | null,
): Exclude<StressSeverity, "breaks"> {
  const noiDrop = noiDeltaPct != null ? -noiDeltaPct : 0;          // positive = worsened
  const dscrDrop =
    baseDscr != null && stressedDscr != null ? baseDscr - stressedDscr : 0;
  const absScore = Math.abs(scoreDelta);

  // A verdict flip is a real signal — the deal stops clearing its cutoff — but
  // it is a performance move, not a survival failure. It floors severity at
  // "major" so the scenario still ranks to the top, without claiming the deal
  // breaks. The flip itself is reported separately (verdictFlipped/flipCount).
  if (verdictFlipped) return "major";

  // "Major" — meaningful financial impact, even when score is floored.
  if (noiDrop >= 20 || dscrDrop >= 0.15 || absScore >= 12) return "major";

  // "Moderate" — noticeable but contained.
  if (noiDrop >= 10 || dscrDrop >= 0.08 || absScore >= 6) return "moderate";

  return "minor";
}

const SEVERITY_RANK: Record<StressSeverity, number> = {
  breaks: 4,
  major: 3,
  moderate: 2,
  minor: 1,
};

// ── Public API ─────────────────────────────────────────────────────────────
export function computeSensitivityOverlays(
  input: ScoringInput,
  options: SensitivityComputeOptions = {},
): SensitivityOverlayResult {
  const baseResult: ScoringResult = calculateScore(input);
  const baseScore = baseResult.overall_score;
  const baseDscr = baseResult.derived.dscr > 0 ? baseResult.derived.dscr : null;
  const baseNoi = baseResult.derived.baseline_noi || baseResult.derived.noi_in_place || baseResult.derived.noi_stabilized || null;
  // IRR validity: finite, non-zero, and within realistic [-50%, 50%] band.
  const isValidIrr = (v: number) => Number.isFinite(v) && v !== 0 && Math.abs(v) <= 50;
  const baseIrr = isValidIrr(baseResult.derived.proxy_irr) ? baseResult.derived.proxy_irr : null;
  const baseVerdict = baseResult.verdict_label;

  const overlays: StressOverlay[] = STRESSES.map((def) => {
    const applicable = def.isApplicable(input);
    if (!applicable) {
      return {
        id: def.id,
        group: def.group,
        groupLabel: GROUP_LABEL[def.group],
        label: def.label,
        description: def.buildDescription(input),
        applicable: false,
        baseScore,
        stressedScore: baseScore,
        scoreDelta: 0,
        baseDscr,
        stressedDscr: baseDscr,
        dscrDelta: null,
        baseNoi,
        stressedNoi: baseNoi,
        noiDeltaPct: null,
        baseIrr,
        stressedIrr: baseIrr,
        irrDelta: null,
        baseVerdict,
        stressedVerdict: baseVerdict,
        verdictFlipped: false,
        severity: "minor",
      };
    }
    const stressedInput = def.apply(input);
    const stressed = calculateScore(stressedInput);
    const sScore = stressed.overall_score;
    const sDscr = stressed.derived.dscr > 0 ? stressed.derived.dscr : null;
    const sNoi = stressed.derived.baseline_noi || stressed.derived.noi_in_place || stressed.derived.noi_stabilized || null;
    const sIrr = isValidIrr(stressed.derived.proxy_irr) ? stressed.derived.proxy_irr : null;

    // ── Institutional break-condition enforcement ──────────────────────────
    // Broken scenarios MUST surface as NO-GO. Downstream UI runs the value
    // through verdictShort()/getVerdictDisplay(), which is the single source of
    // truth for user-facing labels (GO / PROCEED WITH CAUTION / PASS ON DEAL).
    // A scenario the deal survives keeps whatever verdict the engine gave it —
    // a stressed MAYBE is reported as a MAYBE, not rewritten into a NO-GO.
    const isBroken = scenarioBreaks(sScore, sDscr);

    const effectiveStressedVerdict: "GO" | "MAYBE" | "NO-GO" = isBroken
      ? "NO-GO"
      : stressed.verdict;
    // Emit the canonical verdict kind. Display layers MUST format via
    // verdictShort()/verdictLong() — never render this raw.
    const sVerdict = effectiveStressedVerdict;
    const flipped =
      (baseResult.verdict === "GO" && effectiveStressedVerdict !== "GO") ||
      (baseResult.verdict === "MAYBE" && effectiveStressedVerdict === "NO-GO") ||
      (baseResult.verdict !== "NO-GO" && effectiveStressedVerdict === "NO-GO");

    return {
      id: def.id,
      group: def.group,
      groupLabel: GROUP_LABEL[def.group],
      label: def.label,
      description: def.buildDescription(input),
      applicable: true,
      baseScore,
      stressedScore: sScore,
      scoreDelta: sScore - baseScore,
      baseDscr,
      stressedDscr: sDscr,
      dscrDelta: baseDscr != null && sDscr != null ? sDscr - baseDscr : null,
      baseNoi,
      stressedNoi: sNoi,
      noiDeltaPct: baseNoi && baseNoi > 0 && sNoi != null ? ((sNoi - baseNoi) / baseNoi) * 100 : null,
      baseIrr,
      stressedIrr: sIrr,
      irrDelta: baseIrr != null && sIrr != null ? sIrr - baseIrr : null,
      baseVerdict,
      stressedVerdict: sVerdict,
      verdictFlipped: flipped,
      severity: isBroken
        ? "breaks"
        : classifySeverity(sScore - baseScore, flipped, sDscr, baseDscr, baseNoi && baseNoi > 0 && sNoi != null ? ((sNoi - baseNoi) / baseNoi) * 100 : null),
    };
  });

  const byGroup: Partial<Record<StressGroup, StressOverlay[]>> = {};
  overlays.forEach((o) => {
    (byGroup[o.group] ??= []).push(o);
  });

  // Worst applicable overlay = highest severity rank, then most negative score delta
  const applicable = overlays.filter((o) => o.applicable);
  const worst = applicable.length > 0
    ? applicable.reduce((acc, cur) => {
        const accRank = SEVERITY_RANK[acc.severity];
        const curRank = SEVERITY_RANK[cur.severity];
        if (curRank > accRank) return cur;
        if (curRank < accRank) return acc;
        return cur.scoreDelta < acc.scoreDelta ? cur : acc;
      })
    : null;

  // Fragility under baseline (independent of stress flips).
  const fragilityReasons: string[] = [];
  if (baseDscr != null && baseDscr < 1.0) fragilityReasons.push(`DSCR is ${baseDscr.toFixed(2)}x — below break-even debt coverage.`);
  if (typeof options.noiTrend === "string" && /declin|fall|drop/i.test(options.noiTrend)) fragilityReasons.push("T12 NOI trend is declining.");
  if (typeof options.dataConfidence === "number" && options.dataConfidence < 55) fragilityReasons.push(`Data confidence is low (${Math.round(options.dataConfidence)}%).`);
  if (typeof options.decisionVerdict === "string" && /pass|caution|no/i.test(options.decisionVerdict)) {
    fragilityReasons.push(`Final decision is ${options.decisionVerdict.toUpperCase()} — sensitivity cannot read as stable.`);
  }

  return {
    base: { score: baseScore, verdict: baseVerdict, dscr: baseDscr, noi: baseNoi, irr: baseIrr },
    overlays,
    byGroup,
    worst,
    flipCount: applicable.filter((o) => o.verdictFlipped).length,
    applicableCount: applicable.length,
    fragility: { isFragile: fragilityReasons.length > 0, reasons: fragilityReasons },
  };
}

export const SEVERITY_LABEL: Record<StressSeverity, string> = {
  minor: "Minor",
  moderate: "Moderate",
  major: "Major",
  breaks: "Breaks deal",
};
